import React, { useEffect } from 'react';

import { useFormik } from 'formik-next';
import queryString from 'query-string';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import { useHistory } from 'react-router-dom';

import { theme } from '@waglabs/base-theme';
import { Typography } from '@waglabs/typography';

import { Container } from '../../../../../../components/Container';
import { DirectBookWalkerChip } from '../../../../../../components/DirectBookWalkerChip';
import { LoadingBoneOverlay } from '../../../../../../components/LoadingBoneOverlay';
import {
  NAME_ENTER_PET_INFO,
  NAME_PET_INFO_ENTERED,
} from '../../../../../../components/Tracker/Segment/events';
import {
  PetStrings,
  Routes,
} from '../../../../../../constants';
import {
  useGetAuth,
  useUploaderV2,
  useSegmentTrack,
  useGetUrlQuery,
} from '../../../../../../hooks';
import { useGetWalkerFromUrlQuery } from '../../../../../../hooks/useGetWalkerFromUrlQuery';
import { usePostOwnerPetsMutation } from '../../../../../../slices/owners/v6';
import { petsSlice } from '../../../../../../slices/pets';
import { usePutPetImageMutation } from '../../../../../../slices/pets/v6/petsApi';
import { webApp } from '../../../../../../types/webapp';
import {
  isInitialValid,
  tryCatch,
} from '../../../../../../utils';
import { getPetSizeFromWeight } from '../../../../../../utils/getPetSizeFromWeight';

import {
  PetInfoPart2Form,
  PetInfoPart2FormProps,
  FormFields,
  schema,
} from './components/PetInfoPart2Form';

export const PetInfoPart2 = () => {
  const {
    push,
  } = useHistory();

  const queryParams = useGetUrlQuery();

  const walker = useGetWalkerFromUrlQuery();

  const dispatch = useDispatch();

  const pet = useSelector(
    petsSlice.selectors.getById(queryParams.petId as string),
  );

  const [
    executePostOwnerPets,
    {
      isLoading: isPostOwnerPetsLoading,
    },
  ] = usePostOwnerPetsMutation();

  const {
    ownerID,
  } = useGetAuth();

  const {
    execute: executeUploader,
    isLoading: isUploaderLoading,
  } = useUploaderV2();

  const [
    executePutPetImage,
    {
      isLoading: isPutPetImageLoading,
    },
  ] = usePutPetImageMutation();

  const {
    execute: segmentTrack,
  } = useSegmentTrack();

  useEffect(() => {
    segmentTrack({
      event: NAME_ENTER_PET_INFO,
    });
  }, []);

  const handleImageUpload = async (imageUrl: string) => {
    if (!imageUrl) {
      return undefined;
    }

    const blob = await fetch(imageUrl)
      .then((response) => response.blob());

    const media = new File([blob], `avatar-${Date.now()}`);

    const response = await executeUploader({
      media,
      mediaItemType: 'primary',
      refEntityType: 'dog',
    });

    return response;
  };

  const formikValues = useFormik({
    initialValues: schema.cast(undefined),
    isInitialValid,
    validationSchema: schema,
    onSubmit: async (values) => {
      const imageUrl = pet.imageUrl || '';

      const gender = values[FormFields.PetGender]?.toLowerCase() === PetStrings.Gender.Male
        ? PetStrings.Gender.Male
        : PetStrings.Gender.Female;

      const updatedPet: webApp.V6.Pet = {
        ...pet,
        gender,
        size: getPetSizeFromWeight(values[FormFields.PetWeight] || PetStrings.Weight.Small)
          || PetStrings.Size.Small,
        sterile: values[FormFields.PetSterilized] || PetStrings.Sterile.Unspecified,
      };

      const payload = {
        ownerIdentifier: String(ownerID),
        birthday: (updatedPet.birthday && new Date(updatedPet.birthday).toISOString()) || '',
        breed: updatedPet.breed || '',
        gender,
        name: updatedPet.name || '',
        size: updatedPet.size,
        sterile: updatedPet.sterile,
      };

      const [createdPet, createdPetErr] = await tryCatch(() => (
        executePostOwnerPets(payload).unwrap()
      ));

      if (createdPetErr) {
        return;
      }

      await tryCatch(async () => {
        const imageUploadResponse = await handleImageUpload(imageUrl);

        if (imageUploadResponse) {
          await executePutPetImage({
            petIdentifier: createdPet.uuid,
            file: imageUploadResponse,
          }).unwrap();
        }
      });

      dispatch(
        petsSlice.actions.update(updatedPet),
      );

      segmentTrack({
        event: NAME_PET_INFO_ENTERED,
        data: {
          pet_weights: [updatedPet.size],
          pet_genders: [gender],
          pet_steriles: [updatedPet.sterile],
        },
      });

      const nextUrl = queryString.stringifyUrl({
        url: Routes.SignupAddress,
        query: queryParams,
      });

      push(nextUrl);
    },
  });

  const title = `Describe ${pet?.name}`;
  const isLoading = isPostOwnerPetsLoading || isUploaderLoading || isPutPetImageLoading;

  return (
    <Container>
      {walker.isValid && (
        <DirectBookWalkerChip
          value={walker}
          my="16px"
        />
      )}

      <Typography
        fontFamily="muliBold"
        fontSize="22px"
        color={theme.colors.charcoal}
      >
        {title}
      </Typography>

      {isLoading && (
        <LoadingBoneOverlay />
      )}

      <PetInfoPart2Form
        formik={formikValues as unknown as PetInfoPart2FormProps['formik']}
      />
    </Container>
  );
};
